import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import cloneDeep from 'lodash.clonedeep';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import Logger from 'browser-logging/src/browser/Logger';

import { IBudget, IPage } from 'common/src/types/interfaces';

import {
  addBudget,
  getAveragedPriorFiscalYearsBudget,
  getBudgets,
  updateBudget,
} from 'portal/src/api/BudgetsService';
import useAuth from 'portal/src/auth/useAuth';
import Stepper, { Step } from 'portal/src/components/common/Stepper';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';

import useSnackbarHandler from '../../../../utils/Snackbar/useSnackbarHandler';
import { BudgetSummary } from './Steps/BudgetSummary/BudgetSummary';
import CommodityCostAdjustmentForm from './Steps/CommodityAdjustment/CommodityAdjustmentForm';
import BudgetFiscalYearForm from './Steps/SelectBudgetFiscalYear/BudgetFiscalYearForm';

export const BudgetForm = ({
  entity = { monthlyDetails: [], total: 0 },
  handleBackAction,
}: {
  entity?: Partial<IBudget>;
  handleBackAction: () => void;
}) => {
  const { getUser } = useAuth();
  const snackbarHandler = useSnackbarHandler();
  const { organizationId } = getUser();
  const [fetchingData, setFetchingData] = useState(false);
  const [params] = useSearchParams();
  const isCopyBudget = Boolean(params.get('copy')) ?? false;
  const isImportPriorFiscalYear =
    Boolean(params.get('importfiscalyear')) ?? false;
  const initialFiscalMonth = params.get('month') ?? 6;

  const {
    data: budgets,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['budgets'],
    queryFn: async () => await getBudgets(),
    initialData: { data: [] } as IPage<IBudget>,
  });

  const {
    formState: { isDirty, isValid },
    handleSubmit,
    ...fiscalYearFormControl
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: entity.name ?? '',
      fiscalYear:
        entity.fiscalYear && !isCopyBudget
          ? new Date(
              entity.fiscalYear,
              entity.id ? entity.fiscalStartMonth : Number(initialFiscalMonth),
              1,
            )
          : null,
      adjustment: 0.0,
      importFiscalYears: [],
      facilities: [],
      serviceTypes: [],
    },
  });

  const [loadingPriorFisYear, setLoadingPriorFisYear] = useState(false);
  const [budgetSummary, setBudgetSummary] = useState(getBudgetSummary(entity));
  const [commodityAdjustments, setCommodityAdjustments] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    if (isError) {
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    }
  }, [isError, snackbarHandler]);

  function applyCommodityAdjustments(
    budget: Partial<IBudget>,
    adjustments: Record<string, number>,
  ): Partial<IBudget> {
    const newBudget = cloneDeep(budget);
    let total = 0;

    newBudget.monthlyDetails = newBudget.monthlyDetails.map(
      ({ cost, serviceType, ...rest }) => {
        const adjustedCost = adjustments[serviceType]
          ? cost * (adjustments[serviceType] / 100 + 1)
          : cost;
        total += adjustedCost;
        return { ...rest, serviceType, cost: adjustedCost };
      },
    );

    newBudget.total = total;
    return newBudget;
  }

  async function fecthBudget(): Promise<void> {
    try {
      const selectedFiscalYears =
        fiscalYearFormControl.getValues('importFiscalYears');
      if (selectedFiscalYears.length === 0) return;

      // Filters and adjustment values from the form
      const selectedFacilities = fiscalYearFormControl.getValues('facilities');
      const selectedServiceTypes =
        fiscalYearFormControl.getValues('serviceTypes');
      const adjustment = fiscalYearFormControl.getValues('adjustment');

      const finalBudget = await getAveragedPriorFiscalYearsBudget({
        fiscalYears: selectedFiscalYears,
        fiscalMonth: Number(initialFiscalMonth),
        facilityIds: selectedFacilities,
        serviceTypes: selectedServiceTypes,
        adjustment: adjustment || 0,
      });

      const finalBudgetSummary = getBudgetSummary({
        ...entity,
        monthlyDetails: finalBudget.monthlyDetails,
        total: finalBudget.total,
      });

      setBudgetSummary(finalBudgetSummary);
    } catch (err) {
      Logger.captureError(err as Error);
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    } finally {
      setFetchingData(false);
      setLoadingPriorFisYear(false);
    }
  }

  function getBudgetSummary(budget: Partial<IBudget>): Partial<IBudget> {
    const budgetSummary = cloneDeep(budget);
    const adjustment =
      1 + (fiscalYearFormControl.getValues('adjustment') ?? 0) / 100;

    if (isCopyBudget && adjustment !== 1) {
      let total = 0;
      budgetSummary.monthlyDetails = budgetSummary.monthlyDetails.map(
        ({ cost, ...MDetail }) => {
          const newCost = cost * adjustment;
          total += newCost;
          return { ...MDetail, cost: newCost };
        },
      );

      budgetSummary.total = total;
    }
    return budgetSummary;
  }

  const budgetSummaryComponent = () => {
    const { getValues } = fiscalYearFormControl;
    const fiscalYear = getValues('fiscalYear')?.getFullYear();

    if (loadingPriorFisYear) {
      return (
        <Stack spacing={2} margin={10}>
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
          <Typography variant="h6" textAlign="center">
            Please wait, preparing the budget...
          </Typography>
        </Stack>
      );
    }

    return (
      <BudgetSummary
        fiscalYear={fiscalYear}
        budget={budgetSummary}
        onChange={setBudgetSummary}
      />
    );
  };

  const steps: Step[] = [
    {
      disableNext: !isDirty || !isValid,
      nextBtnCondition: async () => {
        let isSuccess = false;

        await handleSubmit(
          (data) => {
            const year = (data.fiscalYear as Date).getFullYear();
            const existentFY = budgets.data.some(
              ({ fiscalYear }) => fiscalYear === year,
            );

            if (existentFY) {
              snackbarHandler.warning(
                `A budget already exists for the ${year} fiscal year.`,
              );
            }

            setLoadingPriorFisYear(isImportPriorFiscalYear);
            if (isImportPriorFiscalYear) {
              void fecthBudget();
            }

            if (isCopyBudget) {
              setBudgetSummary(getBudgetSummary(entity));
            }

            isSuccess = true;
          },
          () => {
            isSuccess = false;
          },
        )();

        return isSuccess;
      },
      stepLabel: `Select${isCopyBudget ? `/Copy ${entity.fiscalYear}` : '/Import'} Fiscal Year Budget`,
      view: (
        <BudgetFiscalYearForm
          formControl={fiscalYearFormControl}
          accesibility={{
            isCopy: isCopyBudget,
            importPriorFiscalYear: isImportPriorFiscalYear,
          }}
        />
      ),
    },
    {
      nextBtnCondition: () => {
        const updatedBudget = applyCommodityAdjustments(
          budgetSummary,
          commodityAdjustments,
        );
        setBudgetSummary(updatedBudget);
        return true;
      },
      stepLabel: 'Adjust Commodity Costs',
      view: (
        <CommodityCostAdjustmentForm
          budget={budgetSummary}
          onAdjust={(updatedRates) => setCommodityAdjustments(updatedRates)}
          selectedYears={fiscalYearFormControl.getValues('importFiscalYears')}
        />
      ),
    },
    {
      nextBtnCondition: async () => {
        let isSuccess = true;
        setFetchingData(true);

        try {
          let snackMessage: string;
          const document = {
            ...budgetSummary,
            name: fiscalYearFormControl.getValues('name'),
            fiscalYear: fiscalYearFormControl
              .getValues('fiscalYear')
              .getFullYear(),
            fiscalStartMonth: Number(initialFiscalMonth),
          };

          if (entity.id && !isCopyBudget) {
            snackMessage = MESSAGES.BUDGET.UPDATED;
            await updateBudget(entity.id, document);
          } else {
            delete document.id;
            snackMessage = MESSAGES.BUDGET.CREATED;
            await addBudget({ ...document, organizationId });
          }

          handleBackAction();
          snackbarHandler.success(snackMessage);
          isSuccess = true;
        } catch (err) {
          Logger.captureError(err as Error);
          snackbarHandler.error(MESSAGES.GENERIC.ERROR);
          isSuccess = false;
        } finally {
          setFetchingData(false);
        }
        return isSuccess;
      },
      stepLabel: 'Budget Summary / Submit Budget',
      view: budgetSummaryComponent(),
    },
  ];

  return isPending ? (
    <Box display="flex" justifyContent="center" marginTop={12}>
      <CircularProgress aria-label="Loading" />
    </Box>
  ) : (
    <Stepper
      defaultStep={entity.id && !isCopyBudget ? 2 : 0}
      isPending={fetchingData}
      isUpdate={!!entity.id}
      handleBackAction={handleBackAction}
      steps={steps}
    />
  );
};
